// Gaps in the diary worth posting about: the same diary call and normaliser
// the diary screen uses. A gap is at least 45 minutes of a working day with
// nothing in it, starting an hour from now or later. Only diaries the person
// may see count (diary sharing), and a self-employed stylist's own only.
import moment from 'moment'
import { hairdresserService } from '../../../../../_services/hairdresser.service'
import { diarySharingService } from '../../../../../_services/diarySharing.service'
import { normalizeDiaryResponse } from '../../../../DiaryV2/normalizeData'
import { getAccess, isSelfEmployedMe } from '../../../../permissions/access'
import { dayLabel, minutesOf, isoDay } from './dates'

const MIN_GAP = 45
const FRESH_MS = 2 * 60 * 1000

// 840 -> "2pm", 930 -> "3:30pm"
export const timeLabel = (mins) => {
  const h = Math.floor(mins / 60)
  const m = mins % 60
  const h12 = ((h + 11) % 12) + 1
  return `${h12}${m ? `:${String(m).padStart(2, '0')}` : ''}${h < 12 ? 'am' : 'pm'}`
}

// What leaves the time free: a booking that's been cancelled (3) or was a
// no-show (6), in the diary's own numbers or the words older feeds used, and
// an event marked "Still available for booking"
const NOT_HELD = [3, 6]
const cancelled = (e) => NOT_HELD.includes(Number(e.status)) || /cancel|no.?show|declin/i.test(String(e.status || ''))
const holdsTime = (e) => !cancelled(e) && !(e.type === 'event' && e.isBookingAllowed === true)

// The free stretches [start, end] in one staff member's day, in minutes
export const freeIntervals = (staff, events, fromMins = 0) => {
  if (!staff.isWorking || staff.startMinutes == null || staff.endMinutes == null) return []
  const busy = (events || [])
    .filter(holdsTime)
    .map(e => [e.startMinutes != null ? e.startMinutes : (e.raw && e.raw.startTime), e.endMinutes != null ? e.endMinutes : (e.raw && e.raw.endTime)])
    .filter(([s, e]) => s != null && e != null && e > s)
    .sort((a, b) => a[0] - b[0])
  const out = []
  // Starts on the next quarter hour
  let cursor = Math.ceil(Math.max(staff.startMinutes, fromMins) / 15) * 15
  busy.forEach(([s, e]) => {
    if (s - cursor >= MIN_GAP) out.push([cursor, s])
    cursor = Math.max(cursor, Math.ceil(e / 15) * 15)
  })
  if (staff.endMinutes - cursor >= MIN_GAP) out.push([cursor, staff.endMinutes])
  return out
}

const diaryFor = async (startDate, endDate) => {
  const res = await hairdresserService.getDiaryData({ startDate, endDate })
  return (res && res.data && (res.data.responseData || res.data.data)) || null
}

// ─── Whose diary ───
// The signed-in person's own column (the diary keys staff by user id)
export const myStaffId = () => { try { return String(localStorage.getItem('userId') || '') } catch (e) { return '' } }
// A self-employed stylist posts their own free times only
export const ownTimesOnly = () => { try { return isSelfEmployedMe() } catch (e) { return false } }
// Whose free times a new post starts with: the owner's start on everyone's,
// anyone else's on their own (when they have some)
export const startsOnOwn = () => { try { return ownTimesOnly() || !getAccess().owner } catch (e) { return false } }
export const firstNameOf = (s) => (s && (s.firstName || String(s.name || '').trim().split(/\s+/)[0])) || ''
// The diaries this person may see, as the diary shows them (diary sharing):
// null when there's no limit, or it can't be read (as in the diary)
let seenJob = null
const seenIds = () => {
  if (seenJob && Date.now() - seenJob.at < FRESH_MS) return seenJob.job
  const job = diarySharingService.fetchMyVisibility()
    .then(r => (r && Array.isArray(r.canSee) && r.canSee.length ? r.canSee.map(String) : null))
    .catch(() => null)
  seenJob = { at: Date.now(), job }
  return job
}
// The staff whose free time may go on a post: `own` (a self-employed
// stylist's id) keeps only them; `seen` limits to the diaries they may see
export const staffToPost = (staffMembers, { seen = null, own = null } = {}) => {
  const list = staffMembers || []
  if (own) return list.filter(s => String(s.id) === String(own))
  if (!seen) return list
  const ok = new Set(seen.map(String))
  return list.filter(s => ok.has(String(s.id)))
}
// The diary for some days, and the staff it lets this person post about, in
// the diary's own column order
const readDiary = async (startDate, endDate) => {
  const [data, seen] = await Promise.all([diaryFor(startDate, endDate), seenIds()])
  if (!data) return null
  const own = ownTimesOnly() ? myStaffId() : null
  const order = Array.isArray(data.staffOrder) ? data.staffOrder.map(String) : []
  const rank = (s) => { const i = order.indexOf(String(s.id)); return i === -1 ? order.length : i }
  const day = (date) => {
    const { staffMembers, eventsByStaff } = normalizeDiaryResponse(data, date)
    const staff = staffToPost(staffMembers, { seen, own }).map((s, i) => ({ s, i })).sort((a, b) => (rank(a.s) - rank(b.s)) || (a.i - b.i)).map(x => x.s)
    return { staff, eventsByStaff, rank }
  }
  return { day, own }
}
// One person's view, for a self-employed stylist's own times
const whoOf = (staff) => (staff ? { id: String(staff.id), firstName: firstNameOf(staff) } : null)

// { date, dayLabel, times (up to three, spread through the day), count,
// staff } for the first day in the next `days` with two or more gaps, or null
export const findFreeSlots = async ({ days = 3, now = new Date() } = {}) => {
  const start = moment(now).startOf('day')
  const diary = await readDiary(start.format('YYYY-MM-DD'), moment(start).add(days - 1, 'days').format('YYYY-MM-DD'))
  if (!diary) return null
  for (let i = 0; i < days; i += 1) {
    const day = moment(start).add(i, 'days')
    const date = day.format('YYYY-MM-DD')
    const { staff: staffMembers, eventsByStaff } = diary.day(date)
    const fromMins = i === 0 ? now.getHours() * 60 + now.getMinutes() + 60 : 0
    const starts = new Set()
    const names = new Set()
    staffMembers.forEach((s) => {
      const gaps = freeIntervals(s, eventsByStaff[s.id], fromMins)
      gaps.forEach(([a]) => starts.add(a))
      if (gaps.length) names.add(s.firstName || (s.name || '').split(' ')[0])
    })
    if (starts.size >= 1) {
      const sorted = Array.from(starts).sort((a, b) => a - b)
      // Spread the three picks through the day rather than the first three
      const picks = pickMinutes(sorted, 3)
      return {
        date,
        dayLabel: dayLabel(date, now),
        times: picks.map(timeLabel),
        count: sorted.length,
        staff: Array.from(names).filter(Boolean),
        who: diary.own ? whoOf(staffMembers[0]) : null,
      }
    }
  }
  return null
}

// The week ahead, day by day: how much of each day is free (for Bailey's
// plans to spot the quiet ones). [{ date, dayLabel, count, freeHours, times,
// all, staff, staffIds, byStaff, who }] for days with a gap; today counts from an hour
// from now. byStaff is each stylist's own { id, name, firstName, times, all,
// count }, for "Jess's free times": their hourly free times, as the Text
// tool offers them.
export const freeWeek = async ({ days = 8, now = new Date() } = {}) => {
  const start = moment(now).startOf('day')
  const diary = await readDiary(start.format('YYYY-MM-DD'), moment(start).add(days - 1, 'days').format('YYYY-MM-DD'))
  if (!diary) return null
  const out = []
  for (let i = 0; i < days; i += 1) {
    const day = moment(start).add(i, 'days')
    const date = day.format('YYYY-MM-DD')
    const { staff: staffMembers, eventsByStaff } = diary.day(date)
    const fromMins = i === 0 ? now.getHours() * 60 + now.getMinutes() + 60 : 0
    const starts = new Set()
    const names = new Set()
    const byStaff = []
    let minutes = 0
    staffMembers.forEach((s) => {
      const gaps = freeIntervals(s, eventsByStaff[s.id], fromMins)
      gaps.forEach(([a, b]) => { starts.add(a); minutes += b - a })
      if (!gaps.length) return
      names.add(firstNameOf(s))
      // Their times as the Text tool offers them: each gap's start and every
      // hour after it, so a free day reads 9am, 2pm, 7pm rather than just 8am
      const own = slotsOn([s], eventsByStaff, fromMins)
      byStaff.push({ id: String(s.id), name: s.name || '', firstName: firstNameOf(s), times: pickTimes(own, 3).map(x => x.label), all: own.map(x => x.label), count: own.length })
    })
    if (!starts.size) continue // eslint-disable-line no-continue
    const sorted = Array.from(starts).sort((a, b) => a - b)
    const picks = pickMinutes(sorted, 3)
    out.push({ date, dayLabel: dayLabel(date, now), count: sorted.length, freeHours: Math.round(minutes / 6) / 10, times: picks.map(timeLabel), all: sorted.map(timeLabel), staff: Array.from(names).filter(Boolean), staffIds: byStaff.map(x => x.id), byStaff, who: diary.own ? whoOf(staffMembers[0]) : null })
  }
  return out
}

// ─── Picking times from the diary (Text tool, New post) ───
// Every time someone's free on a day, with who: the start of each gap, then
// each hour on the hour after it while 45 minutes are left, so an empty
// afternoon offers 1pm, 2pm, 3pm rather than just 1pm. Earliest first.
export const slotsOn = (staffMembers, eventsByStaff, fromMins = 0) => {
  const byStart = new Map()
  ;(staffMembers || []).forEach((s) => {
    const name = s.firstName || String(s.name || '').split(' ')[0]
    freeIntervals(s, (eventsByStaff || {})[s.id], fromMins).forEach(([a, b]) => {
      for (let t = a; b - t >= MIN_GAP; t = t === a ? Math.floor(a / 60) * 60 + 60 : t + 60) {
        const slot = byStart.get(t) || { mins: t, label: timeLabel(t), staff: [] }
        if (name && !slot.staff.includes(name)) slot.staff.push(name)
        byStart.set(t, slot)
      }
    })
  })
  return Array.from(byStart.values()).sort((x, y) => x.mins - y.mins)
}

// The next `days` days' free times, day by day: [{ date, dayLabel, slots,
// gaps, staff }], days with none left out; today counts from an hour from
// now. gaps are everyone's free stretches, for checking a time; staff is each
// stylist free that day, in diary order: { id, name, firstName, rank, slots,
// gaps }. Read once and kept for two minutes, so the Text tool and New post
// share one diary read. Null when the diary can't be read.
const kept = new Map()
const salonNow = () => { try { return localStorage.getItem('salonId') || '' } catch (e) { return '' } }
export const freeDays = ({ days = 8, now = new Date() } = {}) => {
  const key = `${salonNow()}:${myStaffId()}:${days}:${isoDay(now)}`
  const hit = kept.get(key)
  if (hit && Date.now() - hit.at < FRESH_MS) return hit.job
  const job = (async () => {
    const start = moment(now).startOf('day')
    const diary = await readDiary(start.format('YYYY-MM-DD'), moment(start).add(days - 1, 'days').format('YYYY-MM-DD'))
    if (!diary) return null
    const out = []
    for (let i = 0; i < days; i += 1) {
      const date = moment(start).add(i, 'days').format('YYYY-MM-DD')
      const { staff: staffMembers, eventsByStaff, rank } = diary.day(date)
      const fromMins = i === 0 ? now.getHours() * 60 + now.getMinutes() + 60 : 0
      const slots = slotsOn(staffMembers, eventsByStaff, fromMins)
      const gaps = staffMembers.flatMap(s => freeIntervals(s, eventsByStaff[s.id], fromMins))
      const staff = staffMembers.map((s) => {
        const own = slotsOn([s], eventsByStaff, fromMins)
        return own.length ? { id: String(s.id), name: s.name || '', firstName: firstNameOf(s), rank: rank(s), slots: own, gaps: freeIntervals(s, eventsByStaff[s.id], fromMins) } : null
      }).filter(Boolean)
      if (slots.length) out.push({ date, dayLabel: dayLabel(date, now), slots, gaps, staff })
    }
    return out
  })().catch(() => null)
  kept.set(key, { at: Date.now(), job })
  job.then((v) => { if (v == null) kept.delete(key) })
  return job
}
export const forgetFreeDays = () => { kept.clear(); seenJob = null }

// The same days for one stylist: their free times only, and the days they
// have none left out. No id (Everyone) gives the days as they are.
export const daysFor = (days, staffId) => {
  if (!staffId) return days || []
  return (days || []).map((d) => {
    const s = (d.staff || []).find(x => x.id === String(staffId))
    return s ? { date: d.date, dayLabel: d.dayLabel, slots: s.slots, gaps: s.gaps, staff: [s] } : null
  }).filter(Boolean)
}
// Everyone free on any of these days, in diary order, with how many free
// times they have: [{ id, name, firstName, count }]. Nameless columns are
// left out, as they can't go on a post.
export const teamIn = (days) => {
  const by = new Map()
  ;(days || []).forEach(d => (d.staff || []).forEach((s) => {
    if (!s.name || /^unknown$/i.test(String(s.name).trim())) return
    const had = by.get(s.id)
    if (had) had.count += s.slots.length
    else by.set(s.id, { id: s.id, name: String(s.name).trim(), firstName: s.firstName, rank: s.rank, count: s.slots.length })
  }))
  return Array.from(by.values()).sort((a, b) => (a.rank || 0) - (b.rank || 0)).map(({ rank, ...m }) => m)
}

// Times as a post keeps them: earliest first (anything that isn't a time,
// from an older post, stays at the end)
export const sortTimes = (list) => {
  const clean = (list || []).map(t => String(t || '').trim()).filter(Boolean)
  const known = clean.filter(t => minutesOf(t) != null).sort((a, b) => minutesOf(a) - minutesOf(b))
  return [...known, ...clean.filter(t => minutesOf(t) == null)]
}
export const hasTime = (list, label) => {
  const m = minutesOf(label)
  return (list || []).some(t => (m == null ? String(t).trim() === String(label).trim() : minutesOf(t) === m))
}
// Tap a time: off when it's on; on (in its place) when there's room
export const toggleTime = (list, label, max = 3) => {
  const now = (list || []).filter(Boolean)
  if (hasTime(now, label)) return sortTimes(now.filter(t => !hasTime([t], label)))
  if (now.length >= max) return sortTimes(now)
  return sortTimes([...now, label])
}
// Whether someone's free at a time: it sits in a gap with 45 minutes left
export const freeAt = (gaps, label) => {
  const m = minutesOf(label)
  return m != null && (gaps || []).some(([a, b]) => a <= m && b - m >= MIN_GAP)
}
// n times spread through the day: the first, the last and evenly between
export const spreadPicks = (slots, n) => {
  const list = slots || []
  if (list.length <= n) return list.slice()
  if (n <= 1) return list.slice(0, 1)
  const picks = []
  for (let i = 0; i < n; i += 1) {
    const s = list[Math.round((i * (list.length - 1)) / (n - 1))]
    if (!picks.includes(s)) picks.push(s)
  }
  return picks
}

// The times a post starts with: spread through the usual hours (starts from
// 9am to 7pm) when enough are free then, so a diary open 5:45am to 11pm still
// starts the post at 9am, 2pm and 7pm rather than 5:45am, 1pm and 11pm
const USUAL = [540, 1140]
export const pickTimes = (slots, n) => {
  const list = slots || []
  const usual = list.filter(s => s.mins >= USUAL[0] && s.mins <= USUAL[1])
  return spreadPicks(usual.length >= n ? usual : list, n)
}
const pickMinutes = (sorted, n) => pickTimes(sorted.map(mins => ({ mins })), n).map(s => s.mins)

// Before sharing: which of a post's times aren't free any more (in one
// stylist's diary when the post is theirs). Unknown (null) when the diary
// can't be read or a time can't be understood.
export const checkTimes = async (date, times, staffId = null) => {
  try {
    const diary = await readDiary(date, date)
    if (!diary) return null
    const { staff, eventsByStaff } = diary.day(date)
    const whose = staffId ? staff.filter(s => String(s.id) === String(staffId)) : staff
    const free = whose.flatMap(s => freeIntervals(s, eventsByStaff[s.id], 0))
    const gone = (times || []).filter(Boolean).filter((t) => {
      const m = minutesOf(t)
      if (m == null) return false
      return !free.some(([a, b]) => a <= m && b - m >= MIN_GAP)
    })
    return { gone }
  } catch (e) {
    return null
  }
}
